import { useEffect, useMemo, useState } from 'react';
import { Card, v } from '@kss/ui';
import { publicCatalogService, type PublicPage } from '@kss/api';
import { Mail, Phone, MessageCircle, MapPin } from 'lucide-react';

/**
 * "Contact us" storefront page — reads the current tenant's active
 * PageContent row keyed on <code>slug === 'contact-us'</code>. The Body
 * column is rendered raw; the four contact fields (Phone / Email /
 * WhatsApp / Address) drive the contact card and a simple
 * mailto:-based "Send message" form. Falls back to a friendly
 * placeholder when no row exists.
 */
export default function Contact() {
  const [page, setPage] = useState<PublicPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const data = await publicCatalogService.getPage('contact-us');
        if (!cancelled) setPage(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const title = page?.title?.trim() || 'Contact us';
  const body = page?.body?.trim() ?? '';
  const phone = page?.contactPhone?.trim() ?? '';
  const email = page?.contactEmail?.trim() ?? '';
  const address = page?.contactAddress?.trim() ?? '';
  const whatsapp = page?.contactWhatsapp?.trim() ?? '';

  const showPlaceholder = !loading && !body && !phone && !email && !address && !whatsapp;

  // Build a mailto: URL — the storefront doesn't own an SMTP path, so
  // the "Send message" form just hands the user's mail client the
  // subject + body pre-filled. Cheaper than shipping a form endpoint.
  const mailtoHref = useMemo(() => {
    if (!email) return '';
    const params = new URLSearchParams();
    if (form.subject) params.set('subject', form.subject);
    const bodyText = form.message
      ? `${form.message}\n\n— ${form.name || 'Anonymous'}${form.email ? ` <${form.email}>` : ''}`
      : '';
    if (bodyText) params.set('body', bodyText);
    const qs = params.toString();
    return qs ? `mailto:${email}?${qs}` : `mailto:${email}`;
  }, [email, form.subject, form.message, form.name, form.email]);

  /**
   * Field errors for the contact form.
   *
   * The previous check was a single boolean over three fields, so the
   * button greyed out with no indication of which field was wrong, and
   * `subject` was unchecked entirely.
   *
   * `subject` matters more than it looks: it is interpolated into a
   * `mailto:` URL via `URLSearchParams`, and the name/email/message go
   * into the body. A newline in the subject of a mailto is a classic
   * header-injection vector in mail clients that parse it loosely, so
   * the rules here bar control characters and cap length rather than
   * relying on the URL encoder alone.
   */
  const contactErrors: Record<string, string | null> = {
    name: v.runRules(form.name, [
      v.required('Name'),
      v.minLength(2, 'Name'),
      v.maxLength(80, 'Name'),
      ...v.safeText('Name'),
    ]),
    email: v.runRules(form.email, [v.required('Email'), v.email()]),
    subject: v.runRules(form.subject, [
      v.maxLength(120, 'Subject'),
      v.allowedChars(/^[^\r\n]*$/, 'cannot contain line breaks', 'Subject'),
      ...v.safeText('Subject'),
    ]),
    message: v.runRules(form.message, [
      v.required('Message'),
      v.minLength(10, 'Message'),
      v.maxLength(2000, 'Message'),
      ...v.safeText('Message'),
    ]),
  };
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const cErr = (k: string) => (touched[k] ? contactErrors[k] ?? undefined : undefined);
  const markC = (k: string) => () => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const valid = !Object.values(contactErrors).some(Boolean);

  return (
    <div className="bg-[var(--color-bg-app)]">
      {/* Hero */}
      <section className="border-b border-[var(--color-border)] bg-[var(--color-bg-topbar)]">
        <div className="mx-auto max-w-5xl px-4 py-10 text-center sm:px-6 sm:py-14">
          <h1 className="text-3xl font-extrabold tracking-tight text-[var(--color-text-primary)] sm:text-4xl">
            {title}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)] sm:text-base">
            We&apos;re here to help — reach out any time.
          </p>
        </div>
      </section>

      {loading ? (
        <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-12">
          <Card className="p-6 text-center text-sm text-[var(--color-text-muted)]">
            Loading…
          </Card>
        </section>
      ) : showPlaceholder ? (
        <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-12">
          <Card className="p-8 text-center">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Contact details coming soon
            </h2>
            <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
              We haven&apos;t published our contact information yet — check back shortly.
            </p>
            {error && (
              <p className="mt-3 text-xs text-[var(--color-text-muted)]">
                (Load hint: {error})
              </p>
            )}
          </Card>
        </section>
      ) : (
        <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12">
          <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
            {/* Left column — body + inline form */}
            <div className="grid gap-6">
              {body && (
                <Card className="p-6 sm:p-8">
                  <div
                    className="page-content-body prose max-w-none text-sm leading-relaxed text-[var(--color-text-secondary)]"
                    dangerouslySetInnerHTML={{ __html: body }}
                  />
                </Card>
              )}

              {email && (
                <Card className="p-5 sm:p-6">
                  <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
                    Send us a message
                  </h2>
                  <p className="mt-1 text-xs text-[var(--color-text-secondary)]">
                    Your default mail client opens with the message pre-filled.
                  </p>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-text-secondary)]">
                      Your name *
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => set('name', e.target.value)}
                        placeholder="Jane Doe"
                        onBlur={markC("name")}
                        className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]"
                      />
                      {cErr("name") && <span className="text-[11px] text-[var(--color-danger)]">{cErr("name")}</span>}
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-text-secondary)]">
                      Your email *
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => set('email', e.target.value)}
                        placeholder="jane@example.com"
                        onBlur={markC("email")}
                        className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]"
                      />
                      {cErr("email") && <span className="text-[11px] text-[var(--color-danger)]">{cErr("email")}</span>}
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-text-secondary)] sm:col-span-2">
                      Subject
                      <input
                        type="text"
                        value={form.subject}
                        onChange={(e) => set('subject', e.target.value)}
                        placeholder="Short subject"
                        onBlur={markC("subject")}
                        className="h-10 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]"
                      />
                      {cErr("subject") && <span className="text-[11px] text-[var(--color-danger)]">{cErr("subject")}</span>}
                    </label>
                    <label className="flex flex-col gap-1 text-xs font-medium text-[var(--color-text-secondary)] sm:col-span-2">
                      Message *
                      <textarea
                        rows={5}
                        value={form.message}
                        onChange={(e) => set('message', e.target.value)}
                        placeholder="How can we help?"
                        onBlur={markC("message")}
                        className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] p-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]"
                      />
                      {cErr("message") && <span className="text-[11px] text-[var(--color-danger)]">{cErr("message")}</span>}
                    </label>
                    <div className="sm:col-span-2">
                      <a
                        href={valid ? mailtoHref : undefined}
                        aria-disabled={!valid}
                        className={
                          'inline-flex h-10 items-center justify-center rounded-md px-5 text-sm font-semibold transition-colors ' +
                          (valid
                            ? 'bg-[var(--color-primary)] text-[var(--color-primary-fg)] hover:bg-[var(--color-primary-hover)]'
                            : 'cursor-not-allowed bg-[var(--color-border)] text-[var(--color-text-muted)]')
                        }
                      >
                        Send message
                      </a>
                    </div>
                  </div>
                </Card>
              )}
            </div>

            {/* Right column — contact card */}
            <div className="grid gap-4">
              <Card className="p-5">
                <h3 className="mb-3 text-base font-semibold text-[var(--color-text-primary)]">
                  Reach us
                </h3>
                <ul className="grid gap-3 text-sm">
                  {phone && (
                    <li className="flex items-start gap-3">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                        <Phone className="h-4 w-4" />
                      </span>
                      <a
                        href={`tel:${phone}`}
                        className="text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                      >
                        {phone}
                      </a>
                    </li>
                  )}
                  {email && (
                    <li className="flex items-start gap-3">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                        <Mail className="h-4 w-4" />
                      </span>
                      <a
                        href={`mailto:${email}`}
                        className="text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                      >
                        {email}
                      </a>
                    </li>
                  )}
                  {whatsapp && (
                    <li className="flex items-start gap-3">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                        <MessageCircle className="h-4 w-4" />
                      </span>
                      <a
                        href={`https://wa.me/${whatsapp.replace(/\D+/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[var(--color-text-primary)] hover:text-[var(--color-primary)]"
                      >
                        Chat on WhatsApp
                      </a>
                    </li>
                  )}
                  {address && (
                    <li className="flex items-start gap-3">
                      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[var(--color-primary-light)] text-[var(--color-primary)]">
                        <MapPin className="h-4 w-4" />
                      </span>
                      <address className="whitespace-pre-line not-italic text-[var(--color-text-secondary)]">
                        {address}
                      </address>
                    </li>
                  )}
                </ul>
              </Card>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
